import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { AnalyticsOverview, ReviewFeed } from '@rp/contracts';
import { api } from '../lib/api';
import { ReviewCard } from '../components/ReviewCard';
import { Button, Card, EmptyState, Input, Segmented, Skeleton } from '../components/ui';
import { haptic } from '../lib/telegram';
import { percent, pluralize } from '../lib/format';
import { mainInsight, verdictColor } from '../lib/insights';

/**
 * Что показывать. Раньше режим и площадка лежали в одном ряду фишек, и выбрать
 * «негатив в 2ГИС» было нельзя: фишки исключали друг друга, хотя описывают
 * разные вещи. Теперь это две независимые оси.
 */
const MODES = [
  { value: 'unanswered', label: 'Ждут ответа', params: { statuses: 'NEW,NEEDS_REPLY,DRAFT_READY' } },
  { value: 'negative', label: 'Негатив', params: { ratings: '1,2,3' } },
  { value: 'all', label: 'Все', params: {} },
] as const;

const PLATFORMS = [
  { value: 'all', label: 'Обе площадки', params: {} },
  { value: 'TWOGIS', label: '2ГИС', params: { platforms: 'TWOGIS' } },
  { value: 'YANDEX', label: 'Яндекс', params: { platforms: 'YANDEX' } },
] as const;

type Mode = (typeof MODES)[number]['value'];
type PlatformFilter = (typeof PLATFORMS)[number]['value'];

/**
 * Лента отзывов обеих площадок.
 *
 * Открывается на «Ждут ответа»: работа начинается с того, что требует действия,
 * иначе первый экран приходится фильтровать руками.
 */
export function FeedPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<Mode>('unanswered');
  const [platform, setPlatform] = useState<PlatformFilter>('all');

  // Переход из аналитики приходит с готовым фильтром в адресе — он главнее.
  const fromUrl = useMemo(() => {
    const params: Record<string, string> = {};
    for (const key of ['statuses', 'ratings', 'platforms', 'sentiment', 'topics', 'branchIds'] as const) {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    }
    return params;
  }, [searchParams]);

  const usingUrlFilter = Object.keys(fromUrl).length > 0;
  const filterParams: Record<string, string> = usingUrlFilter
    ? fromUrl
    : {
        ...(MODES.find((item) => item.value === mode)?.params ?? {}),
        ...(PLATFORMS.find((item) => item.value === platform)?.params ?? {}),
      };

  const query = useInfiniteQuery({
    queryKey: ['feed', filterParams, search],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.feed({ ...filterParams, ...(search ? { search } : {}), cursor: pageParam, limit: 20 }),
    getNextPageParam: (lastPage: ReviewFeed) => lastPage.nextCursor ?? undefined,
  });

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });
  const billing = useQuery({ queryKey: ['billing'], queryFn: api.billing });

  /*
   * Сводка за месяц на первом экране: владелец заходит узнать «как дела», а не
   * листать ленту. При переходе из аналитики с готовым фильтром её прячем —
   * там уже смотрят на конкретный срез.
   */
  const summary = useQuery({
    queryKey: ['analytics', '30'],
    queryFn: () =>
      api.overview({
        from: new Date(Date.now() - 30 * 86_400_000).toISOString(),
        to: new Date().toISOString(),
      }),
  });

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const hasAccounts = (accounts.data?.accounts.length ?? 0) > 0;

  if (accounts.isSuccess && !hasAccounts) {
    const contact = (billing.data?.supportContact ?? 'thiskai').replace(/^@/, '');
    return (
      <EmptyState
        icon="🗺"
        title="Точки не подключены"
        description="Точки подключает разработчик — напишите, и отзывы начнут приходить сюда автоматически."
        action={
          <a
            className="pressable inline-flex h-11 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-accent)] px-5 t-body font-medium text-white"
            href={`https://t.me/${contact}`}
            target="_blank"
            rel="noreferrer"
          >
            Написать разработчику
          </a>
        }
      />
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      {/*
        Шапка плавает над списком: контент продолжает идти под ней, поэтому
        фильтры всегда под рукой и не съедают полосу экрана.
      */}
      <header className="material sticky top-0 z-10 px-4 pt-1 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="t-large-title">Отзывы</h1>
          {!query.isLoading && (
            <span className="shrink-0 t-subhead tabular-nums text-[var(--color-hint)]">
              {pluralize(total, 'отзыв', 'отзыва', 'отзывов')}
            </span>
          )}
        </div>

        <div className="mt-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по тексту и автору"
            type="search"
            autoComplete="off"
          />
        </div>

        <div className="mt-2.5">
          <Segmented
            value={mode}
            options={MODES.map((item) => ({ value: item.value, label: item.label }))}
            onChange={(next) => {
              setMode(next);
              if (usingUrlFilter) setSearchParams({}, { replace: true });
            }}
          />
        </div>

        <div className="mt-2 flex gap-1.5">
          {PLATFORMS.map((item) => {
            const active = !usingUrlFilter && platform === item.value;
            return (
              <button
                key={item.value}
                onClick={() => {
                  haptic.select();
                  setPlatform(item.value);
                  if (usingUrlFilter) setSearchParams({}, { replace: true });
                }}
                aria-pressed={active}
                className="pressable t-footnote rounded-full px-3 py-1 font-medium"
                style={
                  active
                    ? { background: 'var(--color-accent)', color: 'var(--color-on-accent)' }
                    : { background: 'var(--color-surface)', color: 'var(--color-hint)' }
                }
              >
                {item.label}
              </button>
            );
          })}

          {usingUrlFilter && (
            <button
              onClick={() => {
                haptic.select();
                setSearchParams({}, { replace: true });
              }}
              className="pressable t-footnote ml-auto rounded-full px-3 py-1 font-medium text-[var(--color-accent)]"
            >
              Сбросить фильтр
            </button>
          )}
        </div>
      </header>

      <div className="px-4 pb-4">
        {!usingUrlFilter && summary.data && summary.data.totals.reviews > 0 && (
          <Dashboard data={summary.data} onOpen={() => navigate('/analytics')} />
        )}

        {query.isLoading ? (
          <div className="space-y-2 pt-1">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-[132px]" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={mode === 'unanswered' ? '🎉' : '🔍'}
            title={mode === 'unanswered' ? 'Всё отвечено' : 'Ничего не найдено'}
            description={
              mode === 'unanswered'
                ? 'Неотвеченных отзывов нет. Загляните во «Все», чтобы посмотреть историю.'
                : 'Попробуйте изменить фильтр или поисковый запрос.'
            }
          />
        ) : (
          <>
            {items.map((review) => (
              <ReviewCard key={review.id} review={review} onOpen={(id) => navigate(`/feed/${id}`)} />
            ))}
            {query.hasNextPage && (
              <Button
                variant="secondary"
                className="mt-1 w-full"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                Показать ещё
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Короткая сводка над лентой: цифры месяца и вывод одной строкой. */
function Dashboard({
  data,
  onOpen,
}: {
  data: AnalyticsOverview;
  onOpen: () => void;
}) {
  const insight = mainInsight(data);
  const { totals, deltas } = data;

  return (
    <Card className="mt-1 mb-2 p-0">
      <button className="pressable block w-full p-4 text-left" onClick={onOpen}>
        <div className="flex items-start gap-3">
          <span
            className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: verdictColor(insight.verdict) }}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="t-headline">{insight.title}</p>
            <p className="mt-0.5 t-footnote leading-relaxed text-[var(--color-hint)]">{insight.detail}</p>
          </div>
          <span className="shrink-0 pt-1 text-[var(--color-hint)]" aria-hidden="true">›</span>
        </div>

        <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-[var(--color-separator)] pt-3">
          <Stat label="за 30 дней" value={String(totals.reviews)} hint={signedShort(deltas.reviews)} />
          <Stat label="оценка" value={totals.avgRating.toFixed(2)} hint={signedShort(deltas.avgRating, 2)} />
          <Stat label="отвечено" value={percent(totals.responseRate)} hint={`${totals.unanswered} ждут`} />
        </dl>
      </button>
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div>
      <dt className="t-caption text-[var(--color-hint)]">{label}</dt>
      <dd className="mt-0.5 t-title-3 tabular-nums">{value}</dd>
      <dd className="t-caption text-[var(--color-hint)]">{hint}</dd>
    </div>
  );
}

/** Компактная дельта: в сводке важен знак, а не точность до сотых. */
function signedShort(value: number, digits = 0): string {
  if (value === 0) return 'без изменений';
  return `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(digits)}`;
}
